// Wyliczanie domyślnych dat CTA na stronach tras /loty/{from}/{to}.
//
// Sytuacje brzegowe które obsługujemy:
//
//   1. Strefa czasowa serwera (Worker = UTC) ≠ strefa użytkownika
//      Polski użytkownik o 23:30 czasu warszawskiego ma już "jutro" w PL,
//      ale wciąż "dzisiaj" w UTC. Liczymy "dzisiaj" względem Europe/Warsaw,
//      żeby SSR i klient widzieli ten sam dzień bazowy.
//
//   2. Godzina graniczna doby (00:00, 23:59:59.999, sekundy przestępne)
//      Pracujemy na komponentach kalendarzowych (rok/mies/dzień), nie na
//      delcie milisekund — dzięki temu nigdy nie zgubimy/dodamy doby przez
//      DST ani sekundę przestępną.
//
//   3. Przejście przez miesiąc/rok (np. 5 grudnia + 30 dni → 4 stycznia)
//      Date(year, monthIndex, day + N) automatycznie normalizuje przeciążone
//      pole "day" — JS przeniesie nadwyżkę do kolejnych miesięcy/lat.
//
//   4. DST (zmiana czasu letniego/zimowego)
//      Dodawanie milisekund przez doby DST przesuwa o 23 lub 25 godzin
//      i przy granicy doby może zwrócić poprzedni dzień. Dodajemy dni
//      kalendarzowo + formatujemy ISO ręcznie zamiast `toISOString()`,
//      które zawsze formatuje w UTC.

const DEFAULT_TZ = "Europe/Warsaw";
const DEPART_OFFSET_DAYS = 30;
const RETURN_OFFSET_DAYS = 37;

/**
 * Zwraca komponenty roku/miesiąca/dnia "dzisiaj" w podanej strefie czasowej.
 * Używa Intl.DateTimeFormat (zawsze poprawnie radzi sobie z DST i offsetami).
 */
function todayPartsInTimeZone(timeZone: string): { y: number; m: number; d: number } {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  // en-CA daje format YYYY-MM-DD, ale używamy formatToParts dla pewności.
  const parts = fmt.formatToParts(new Date());
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");
  return { y: get("year"), m: get("month"), d: get("day") };
}

/**
 * Formatuje (rok, miesiąc, dzień) → "YYYY-MM-DD" bez konwersji UTC.
 * Pad zerami zachowuje 4-cyfrowy rok, więc rok 9999 → "9999-12-31".
 */
function formatIso(y: number, m: number, d: number): string {
  const pad = (n: number, len = 2) => String(n).padStart(len, "0");
  return `${pad(y, 4)}-${pad(m)}-${pad(d)}`;
}

/**
 * Dodaje N dni kalendarzowych do podanej daty (rok/miesiąc/dzień).
 * `new Date(y, monthIndex, d + N)` normalizuje nadmiar dni — przejście
 * przez miesiąc, rok i lata przestępne działa automatycznie.
 */
function addCalendarDays(y: number, m: number, d: number, days: number): {
  y: number; m: number; d: number;
} {
  // monthIndex jest 0-based w konstruktorze Date; m jest 1-based, więc m - 1.
  // Używamy konstruktora "lokalnego", ale od razu czytamy UTC pola żeby uniknąć
  // wpływu strefy serwera — pracujemy na komponentach, nie na timestampie.
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return {
    y: date.getUTCFullYear(),
    m: date.getUTCMonth() + 1,
    d: date.getUTCDate(),
  };
}

export interface DefaultRouteDates {
  /** ISO YYYY-MM-DD daty wylotu (today + 30 dni). */
  dd: string;
  /** ISO YYYY-MM-DD daty powrotu (today + 37 dni). */
  rd: string;
  /** Bazowe "dzisiaj" w Europe/Warsaw (do dalszych wyliczeń, np. min={today}). */
  today: string;
}

/**
 * Główna funkcja używana przez loader trasy oraz testy.
 * Domyślnie liczy "dzisiaj" w strefie Europe/Warsaw.
 */
export function computeDefaultRouteDates(
  options: { now?: Date; timeZone?: string; departOffset?: number; returnOffset?: number } = {},
): DefaultRouteDates {
  const tz = options.timeZone ?? DEFAULT_TZ;
  // Pozwalamy wstrzyknąć "now" w testach. W produkcji bierzemy aktualny czas.
  const base = options.now
    ? extractParts(options.now, tz)
    : todayPartsInTimeZone(tz);

  const depart = addCalendarDays(base.y, base.m, base.d, options.departOffset ?? DEPART_OFFSET_DAYS);
  const ret = addCalendarDays(base.y, base.m, base.d, options.returnOffset ?? RETURN_OFFSET_DAYS);

  return {
    today: formatIso(base.y, base.m, base.d),
    dd: formatIso(depart.y, depart.m, depart.d),
    rd: formatIso(ret.y, ret.m, ret.d),
  };
}

function extractParts(date: Date, timeZone: string): { y: number; m: number; d: number } {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = fmt.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");
  return { y: get("year"), m: get("month"), d: get("day") };
}
